import {Component, EventEmitter, Input, OnChanges, Output, ViewChild} from '@angular/core';
import { PaginationInstance } from 'ngx-pagination';
import { interval, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HumanInteractionService } from '../../../../services/human-interaction.service';
import { WebClientConfigurationService } from '../../../../services/configuration/web-client-configuration.service';
import { WfTableComponent } from '../../../../common/table-sort/wf-table.component';
import { AccessControlService } from '../../../../services/access-control.service';
import { SortDirection } from '../../../../common/table-sort/table-sort.component';
import { Utils } from '../../../../services/utils';
import { DateTimeService } from '../../../../services/date-time.service';
import { InteractionFilterModalComponent } from '../../interaction-filter-modal/interaction-filter-modal.component';
import { GenericModalService } from '../../../../services/generic-modal.service';
import { DomSanitizer } from '@angular/platform-browser';
import {HumanInteractionTokenResult} from '../../../../Models/humanInteractionTokenResult';
import {BusinessProcessService} from '../../../../services/business-process.service';

@Component({
  selector: 'app-queue-grid',
  templateUrl: './queue-grid.component.html',
  styleUrls: ['./queue-grid.component.css']
})
export class QueueGridComponent implements OnChanges {

  config: PaginationInstance;
  gridItems$: Observable<any[]>;
  userStatePermissions = {};
  p = 1;
  total: number;
  errorMessage: string;
  hasData: boolean;
  columns: any[];
  rows: any[];
  pleaseWait: boolean;
  sortDirection: SortDirection;
  firstColumnSort: any;
  utils: Utils;
  usedClasses = [];
  lastFilter;
  interactionFilterComponent: any = InteractionFilterModalComponent;
  private _filter: any;
  @Input() customQueue = false;
  @Input() title: string;
  @Input() pageConfig: any;
  @Input() uniqueId: string;
  @Input() queueConfig: any;
  @Input() columnPropertyName: string;
  @Input()
  set filter(filter: any) {
    if (filter) {
      this._filter = filter;
      this._filter.pageIndex = 1;
      this.gridItems$ = new Observable<any[]>();
      this.pleaseWait = true;
      this.getEntries(1);
    }
  }
  @Input() hasFilter = true;
  @Input() reportType = '';
  @Input() customInteractionQueues: any[];
  @Input() sortData = true;
  @Output() searchDone: EventEmitter<any> = new EventEmitter();
  @ViewChild(WfTableComponent, { static: false }) wfDataTable: WfTableComponent;

  constructor(
    private humanInteractionService: HumanInteractionService,
    private webClientConfig: WebClientConfigurationService,
    private accessControlService: AccessControlService,
    private dateTimeService: DateTimeService,
    private domSanitizer: DomSanitizer,
    private modalService: GenericModalService,
    private bpService: BusinessProcessService
  ) { }

  ngOnChanges() {
    if (this.pageConfig && this.queueConfig) {
      this.columns = this.queueConfig[this.columnPropertyName].sort((a, b) => a.columnOrder - b.columnOrder);

      this.sortDirection = this.queueConfig.orderDirection;
      this.firstColumnSort = this.queueConfig.orderByColumn ? this.queueConfig.orderByColumn.name : null;
      if (this.customQueue) {
        this._filter.orderByColumnId = this.queueConfig.orderByColumn ? this.queueConfig.orderByColumn.columnOrder + 1 : null;
      } else {
        this._filter.orderByColumnId = this.queueConfig.orderByColumn ? this.queueConfig.orderByColumn.id : null;
      }
      this._filter.orderDirection = this.queueConfig.orderDirection;
      interval(this.pageConfig.refreshInterval * 1000)
        .subscribe(() => {
          this.getPagedInteractionQueueItems(this._filter.pageIndex);
        });
      this.getPagedInteractionQueueItems(1);
    }
  }

  getPagedInteractionQueueItems(page: number) {
    this._filter.pageIndex = page;
    this._filter.pageSize = this.pageConfig.pageSize;

    this.humanInteractionService.getPagedInteractionQueueItems(this._filter)
      .pipe(
        tap( res => {
          this.total = res.total;
          this.p = page;
          this.getPaginationConfig(page, res.total);
        })
      ).subscribe( items => {
          if (this.customQueue && this.reportType === 'suspendedList') {
            this.rows = this.getSuspendedListCustomQueueRows(items['interactionQueueItems']);
            if (!this.customQueue) { this.sortItemValuesByColumn(); }
          } else {
            setTimeout(() => { this.rows = items['interactionQueueItems'];
              if (!this.customQueue) { this.sortItemValuesByColumn(); }}, 1);
          }

          this.hasData = items['interactionQueueItems'].length > 0;
          this.pleaseWait = false;
          this.searchDone.emit(items.total);
      }, error1 => {
        this.errorMessage = error1;
        this.pleaseWait = false;
      }
    );
  }

  getSuspendedListCustomQueueRows(item) {

    item.forEach(f => {
      f.values = [];
      let customInteractionQueueName = '';

      if (this.customInteractionQueues && this.customInteractionQueues.length > 0) {
        customInteractionQueueName = this.customInteractionQueues
          .filter(custom => custom.id === f.customInteractionQueueConfigId).map(m => m.name)[0];
      } else {
        throw new Error('Não existe fila nomeada cadastrada!');
      }
      const creationDate = this.dateTimeService.toLocalStringDateTime(f.creationDate, false);
      const stateModifyDate = this.dateTimeService.toLocalStringDateTime(f.stateModifyDate, false);

      const date1 = new Date(this.dateTimeService.toLocalISOStringDateTime(f.stateModifyDate, false));
      const date2 = new Date();

      let diff = date2.getTime() - date1.getTime();

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      diff -= days * (1000 * 60 * 60 * 24);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      diff -= hours * (1000 * 60 * 60);

      const mins = Math.floor(diff / (1000 * 60));
      diff -= mins * (1000 * 60);

      const seconds = Math.floor(diff / (1000));
      diff -= seconds * (1000);

      const sla = days + ' dia(s), ' + hours + ' hora(s), ' + mins + ' min, ' + seconds + ' seg';

      // reinserindo os valores em ordem a ser exibido no resultado.
      f.values.push(f.businessProcessInstanceId);
      f.values.push(f.businessProcessName);
      f.values.push(customInteractionQueueName);
      f.values.push(f.stateDisplayName);
      f.values.push(creationDate);
      f.values.push(stateModifyDate);
      f.values.push(sla);
    });

    return item;
  }

  sortItemValuesByColumn() {
    if (this.rows && this.rows.length > 0) {
      this.rows.forEach( row => {
        const valuesOrderedByColumn = [];
        this.columns.forEach( column => {
          const columnValue = row.values[column.id - 1];
          valuesOrderedByColumn.push(columnValue);
        });
        row.values = valuesOrderedByColumn;
      });
    }
  }

  setNewFilter(info) {
    if (this.customQueue) {
      this._filter.orderByColumnId = this.columns[this.columns.map(column => column.name).indexOf(info.columnName)].columnOrder + 1;
    } else {
      this._filter.orderByColumnId = this.columns[this.columns.map(column => column.name).indexOf(info.columnName)].id;
    }
    this._filter.orderDirection = info.sortDirection;
    this.getPagedInteractionQueueItems(1);
  }

  getPaginationConfig(_currentPage: number, _totalItems: number) {
    this.config  = {
      id: this.uniqueId,
      itemsPerPage: this.pageConfig.pageSize,
      currentPage: _currentPage,
      totalItems: _totalItems
    };
  }

  getEntries(securableObjectType) {
    const coll = {};
    this.accessControlService.getAllEffectiveEntriesBySecurableObjectTypeAndUser(securableObjectType)
      .subscribe( statePermissions => {
        statePermissions.forEach( entry => {
          const objName = entry.permission.name;
          if (!coll[objName]) {
            coll[objName] = [];
          }
          coll[objName].push(entry.securableObject.securableObjectId);
        });
        this.userStatePermissions = coll;
      }, error1 => {
        this.errorMessage = error1;
        this.userStatePermissions = {};
      });
  }

  checkValue(value) {
    const datePattern = new RegExp(/(\d+)\/(\d+)\/(\d+) (\d{2}):(\d{2}):(\d{2})/g);
    const dt = {dd: null, MM: null, yyyy: null, hh: null, mm: null, ss: null };
    if (datePattern.test(value)) {
      dt.dd = RegExp.$1;
      dt.MM = RegExp.$2;
      dt.yyyy = RegExp.$3;
      dt.hh = RegExp.$4;
      dt.mm = RegExp.$5;
      dt.ss = RegExp.$6;
      return this.dateTimeService.toLocalStringDateTime(
        `${dt.yyyy}-${dt.MM}-${dt.dd}T${dt.hh}:${dt.mm}:${dt.ss}`, false);
    } else {
      return value;
    }
  }

  checkSymbolCode(info: any) {
    if (info === null) {
      return '';
    } else {
      switch (info.fontName.toUpperCase()) {
        case 'TYPICONS':
          const content = info.symbolCode.substr(3, 4).toLowerCase();
          if (!this.usedClasses.includes(content)) {
            const css =
            `.class${content} i::before {
              font-family: 'typicons';
              content: '\\${content}';
              font-size: 1.4rem;
            }`;
            const style = document.createElement('style');
            style.appendChild(document.createTextNode(css));
            document.head.appendChild(style);
            this.usedClasses.push(content);
          }
          return `<span class="class${content}"><i class="typcn"></i></span>`;
        case 'FONTAWESOME':
        case 'AWESOME':
          return this.domSanitizer.bypassSecurityTrustHtml(`<span style="font-size: 1.2rem;"><i class="fa">${info.symbolCode};</i></span>`);
      }
    }
  }

  openFilter() {
    let columnsToFilter;
    if (this.customQueue) {
      columnsToFilter = this.columns.filter(
        column => column.systemField !== 8 && column.systemField !== 5
      );
    } else {
      columnsToFilter = this.columns;
    }
    const objInitialState = {
      columnList: columnsToFilter,
      lastFilter: this.lastFilter,
      isCustomQueue: this.customQueue,
      searchMethod: (componentFilter) => {
        this.lastFilter = componentFilter;
        this._filter.initialDate = componentFilter.initialDate;
        this._filter.finalDate = componentFilter.finalDate;
        this._filter.columnsComparisonType =  componentFilter.columnsComparisonType;
        this._filter.columns = componentFilter.columns;
        this.getPagedInteractionQueueItems(1);
      }
    };
    this.modalService.showObjModal(this.interactionFilterComponent, `Filtro rápido`, objInitialState);
  }

  onInteract(row) {
    this.bpService.openWindowInteraction(row.businessProcessInstanceId, false);
  }

  onView(row) {
    this.bpService.openWindowInteraction(row.businessProcessInstanceId, true);
  }


}
